import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@cbrebuild/blocks';
import attachmentLookupService from '../../services/attachment-lookup-service';
import './attachment-download-center.scss';

/**
 * Simple Download Center for Attachment Lookup - Right Panel Style
 * Matches the "Latest Uploaded File" design pattern
 */
const AttachmentDownloadCenter = ({ toast }) => {
  const [downloads, setDownloads] = useState([]);
  const [downloading, setDownloading] = useState({});

  const fetchDownloads = useCallback(async () => {
    try {
      const jobs = await attachmentLookupService.listBulkDownloadJobs();
      setDownloads(jobs || []);
    } catch (error) {
      console.error('Error fetching downloads');
    }
  }, []);

  // Fetch downloads on mount
  useEffect(() => {
    fetchDownloads();
  }, [fetchDownloads]);

  // Auto-refresh only when there are downloads in 'processing' state
  useEffect(() => {
    const hasProcessingJobs = downloads.some(d => d.status === 'processing');
    
    if (!hasProcessingJobs) {
      return; // No active jobs, don't poll
    }

    // Poll every 10 seconds while there are processing jobs
    const interval = setInterval(fetchDownloads, 10000);
    return () => clearInterval(interval);
  }, [downloads, fetchDownloads]);

  const handleDownload = async (download) => {
    if (!download.download_url) {
      toast('Download not ready yet. Please wait...', 'warning');
      return;
    }

    setDownloading({ ...downloading, [download.id]: true });

    try {
      // Download directly from S3 using presigned URL via programmatic link (same as manage settings)
      const link = document.createElement('a');
      link.setAttribute('href', download.download_url);
      link.setAttribute('style', 'display:none');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast('Download started successfully', 'success');
      
      // Mark as downloaded and refresh list
      try {
        await attachmentLookupService.markBulkDownloadJobDownloaded(download.id);
        fetchDownloads();
      } catch (error) {
        console.error('Error marking download');
      }
    } catch (error) {
      console.error('Download error');
      toast('Failed to download file. Please try again.', 'error');
    } finally {
      setDownloading({ ...downloading, [download.id]: false });
    }
  };

  if (downloads.length === 0) {
    return null;
  }

  return (
    <div className="upload-card-right">
      <h2 className="admin-file-info-header">Download Center</h2>
      <div className="admin-download-center-list">
        {downloads.map((download) => (
          <div key={download.id} className="admin-download-item">
            <div className="admin-download-filename">
              <Icon iconName="file-archive" />
              <span>{download.zip_filename || download.upload_filename}</span>
              {download.status === 'processing' && (
                <span className="download-processing">
                  <Icon iconName="spinner" spin />
                </span>
              )}
            </div>
            {download.status === 'ready' && download.download_url && (
              <button
                onClick={() => handleDownload(download)}
                disabled={downloading[download.id]}
                className="admin-download-icon-button"
                title="Download file"
              >
                {downloading[download.id] ? (
                  <Icon iconName="spinner" spin />
                ) : (
                  <Icon iconName="download" />
                )}
              </button>
            )}
            {download.status === 'error' && (
              <span className="download-error-icon" title={download.error_message}>
                <Icon iconName="exclamation-circle" />
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

AttachmentDownloadCenter.propTypes = {
  toast: PropTypes.func.isRequired,
};

export default AttachmentDownloadCenter;
